import {
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  WindowIcon
} from '@heroicons/react/20/solid'
import { EllipsisVerticalIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { BugAntIcon, PauseCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/solid'
import { capitalize } from 'lodash'
import { useRef } from 'react'
import styled from 'styled-components'

import { ArrowDownRight } from '@/components/Icons'
import Menu, { MenuItem } from '@/components/TailwindUI/Menu'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { Feature, FeatureStatus, TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import cn from '@/utilities/cn'

const StyledArrowDownRight = styled(ArrowDownRight)`
  margin-right: 10px;
  max-height: 12px;
`

type Props = {
  feature: Feature
  readOnly?: boolean
  isSelected?: boolean
  isProcessing?: boolean
  isError?: boolean
  isDeployable?: boolean
  isCopyable?: boolean
  isDebug?: boolean
  onSelect: (isChecked: boolean) => void
  onEdit?: (id: string | undefined) => void
  onRename?: (feature: Feature) => void
  onDuplicate?: (id: string | undefined) => void
  onDelete?: (feature: Feature) => void
  onDeactivate?: (feature: Feature) => void
  onDebug?: (feature: Feature) => void
  onDeploy?: (feature: Feature) => void
  onViewHistory?: (id: string | undefined) => void
}

function formatTriggerType(type: string | undefined, agentsFlagOn: boolean) {
  if (!type) return 'None'
  const map: Record<string, string> = {
    [TriggerTypes.manual]: 'Manual',
    [TriggerTypes.scheduled]: 'Scheduled',
    [TriggerTypes.dataSourceEvent]: 'Data Source Event',
    [TriggerTypes.none]: 'None',
    [TriggerTypes.notificationAction]: 'Submit Action',
    [TriggerTypes.http]: 'HTTP',
    [TriggerTypes.microappView]: `${agentsFlagOn ? 'Agent' : 'App'} UI`,
    [TriggerTypes.featureTrigger]: 'Callable',
    [TriggerTypes.knowledge]: 'Knowledge'
  }
  return map[type] || type
}

export default function FeatureRow({
  feature,
  readOnly = true,
  isSelected = false,
  isProcessing = false,
  isError = false,
  isDeployable = true,
  isCopyable = true,
  isDebug = false,
  onSelect,
  onEdit,
  onRename,
  onDuplicate,
  onDelete,
  onDeactivate,
  onDebug,
  onDeploy,
  onViewHistory
}: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const container = useRef<HTMLButtonElement>(null)
  const isDeployingOrDeactivating =
    feature.status === FeatureStatus.DEPLOYING || feature.status === FeatureStatus.DEACTIVATING
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  return (
    <tr
      key={feature.id}
      data-testid="feature-row"
      className={cn(
        { 'bg-gray-50': isSelected || isProcessing },
        { 'pointer-events-none bg-gray-50': isProcessing },
        'hover:bg-gray-50'
      )}
    >
      <td className="relative px-7 sm:w-12 sm:px-6">
        {isSelected && <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" />}
        {isDeployable && (
          <input
            type="checkbox"
            className={cn(
              'absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600',
              { hidden: readOnly }
            )}
            value={feature.id}
            checked={isSelected}
            disabled={readOnly || isProcessing}
            onChange={e => onSelect(e.target.checked)}
          />
        )}
      </td>
      {}
      <td
        className={cn(
          'whitespace-nowrap py-4 pr-3 text-sm font-medium hover:cursor-pointer',
          isSelected ? 'text-blue-600' : 'text-gray-900'
        )}
        onClick={() => onEdit?.(feature.id)}
      >
        <div className="flex items-center">
          {!isDeployable && <StyledArrowDownRight />}
          <div>{feature.name}</div>
        </div>
      </td>
      {}
      <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 hover:cursor-pointer"
        onClick={() => onEdit?.(feature.id)}
      >
        {formatTriggerType(feature.type, agentsFlagOn)}
      </td>
      {}
      <td
        className="flex w-28 justify-center whitespace-nowrap px-3 py-5 text-sm text-gray-500 hover:cursor-pointer"
        onClick={() => onEdit?.(feature?.id)}
      >
        {isDeployable && (
          <div
            className={cn(
              'w-full items-center rounded-md py-1 px-2 text-center text-xs font-medium ring-1 ring-inset',
              isProcessing
                ? 'bg-amber-50 text-amber-700 ring-amber-600/20'
                : {
                    'bg-gray-50 text-gray-700 ring-gray-600/20':
                      feature.status === FeatureStatus.DRAFT || feature.status === FeatureStatus.INACTIVE,
                    'bg-green-50 text-green-700 ring-green-600/20': feature.status === FeatureStatus.ACTIVE
                  }
            )}
          >
            {isProcessing && !isDeployingOrDeactivating ? 'Pending' : capitalize(feature.status)}
          </div>
        )}
      </td>

      <td className="w-20 whitespace-nowrap py-4 pr-4 text-right text-sm font-medium sm:pr-3">
        <div className="flex flex-row-reverse justify-between">
          <Menu
            data-testid="feature-menu"
            className={cn({ hidden: readOnly }, { 'pointer-events-none': isProcessing })}
            icon={<EllipsisVerticalIcon className="h-7 w-7 hover:stroke-gray-400" />}
            direction="up"
          >
            <div className="py-1">
              <MenuItem onClick={() => onEdit?.(feature.id)}>
                <WindowIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                Open
              </MenuItem>
              <MenuItem onClick={() => onRename?.(feature)}>
                <PencilSquareIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                Rename
              </MenuItem>
              {isCopyable && (
                <MenuItem onClick={() => onDuplicate?.(feature.id)}>
                  <DocumentDuplicateIcon
                    className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  Duplicate
                </MenuItem>
              )}
              <MenuItem onClick={() => onDelete?.(feature)}>
                <TrashIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                Delete
              </MenuItem>
            </div>
            {isDeployable && (
              <div className="py-1">
                {feature.status === 'active' && (
                  <>
                    <MenuItem onClick={() => onDeactivate?.(feature)}>
                      <PauseCircleIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Deactivate
                    </MenuItem>
                    {!isDebug && (
                      <MenuItem onClick={() => onDebug?.(feature)}>
                        <BugAntIcon
                          className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        Enable debug
                      </MenuItem>
                    )}
                  </>
                )}
                {feature.status !== 'active' && (
                  <MenuItem onClick={() => onDeploy?.(feature)}>
                    <RocketLaunchIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Deploy
                  </MenuItem>
                )}
              </div>
            )}
            <div className="py-1">
              <MenuItem onClick={() => onViewHistory?.(feature.id)}>
                <DocumentMagnifyingGlassIcon
                  className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
                View History
              </MenuItem>
            </div>
          </Menu>
          {isError ? (
            <button
              ref={container}
              onMouseEnter={({ clientX }) => {
                if (!tooltipRef.current || !container.current) return
                const { left } = container.current.getBoundingClientRect()

                tooltipRef.current.style.left = clientX - left + 'px'
              }}
              className="group relative inline-block"
              onClick={() => onEdit?.(feature.id)}
            >
              <div className="relative flex items-center pl-1">
                <ExclamationCircleIcon className="stroke-width-2 h-6 w-6 fill-red-50 stroke-red-400 hover:cursor-pointer hover:stroke-red-200" />
              </div>

              <div
                ref={tooltipRef}
                className="invisible absolute top-full z-50 mt-2 whitespace-nowrap rounded-md bg-red-100 p-4 text-red-600 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100"
              >
                <div className="text-left font-semibold">
                  This {agentsFlagOn ? 'capability' : 'feature'}'s workflow is invalid.
                </div>
                <div className="mt-1 text-left">
                  Open to see unresolved issues. Once fixed, test the workflow to ensure validity.
                </div>
              </div>
            </button>
          ) : (
            <div></div>
          )}
        </div>
      </td>
    </tr>
  )
}
